import type { ColKey, Column, GridApi, IRowNode, SelectionEventSourceType } from "ag-grid-community";

import type TitleCellEditor from "~/components/visualization/list/cellEditors/TitleCellEditor.vue";
import { isDefined } from "~/shared/common";
import { RelationshipKindKind, VisualizationItemType } from "~/shared/enums";
import type { Task, VisualizationItem, VisualizationItemTask } from "~/shared/types";
import type { useAppStore } from "~/stores";
import type InboxView from "~/views/InboxView.vue";

type AppStore = ReturnType<typeof useAppStore>;
type TaskRowNodeOptions = { selectedOverride?: boolean; rowIndex?: number };
type HardDisplayTaskContainer = { hardDisplayTask(task: Task): Promise<void> };
type TaskVisualizationContainer = HardDisplayTaskContainer & {
  getAllTasks(): Task[];
  scrollToTask(duid: string): void;
  startEditingTask(duid: string): void;
};

export type Visualization = {
  count(): number;
  getSelected(): VisualizationItemTask[];
  getAll(): VisualizationItemTask[];
  getChildren(duid: string | null): VisualizationItemTask[];
  scrollTo(duid: string): void;
  selectAndScrollTo(duid: string, options?: { deselectOthers?: boolean }): void;
  getHtml(duid: string): Element | null;
  selectAll(): void;
  deselectAll(): void;
  editTitle(duid: string): void;
  hardDisplayTask(task: Task): Promise<void>;
  // TODO still fully row-related - remove?
  getRow(duid: string): IRowNode<VisualizationItemTask> | undefined;
  getRowByIndex(index: number): IRowNode<VisualizationItemTask> | undefined;
  getSelectedRows(): IRowNode<VisualizationItemTask>[];
  getAllRows(): IRowNode<VisualizationItemTask>[];
};

export const makeVisualizationItemTask = (task: { duid: string; absentee?: boolean }): VisualizationItemTask => ({
  itemType: VisualizationItemType.TASK,
  duid: task.duid,
  absentee: task.absentee ?? false,
});

class TaskRowNode implements IRowNode<VisualizationItemTask> {
  destroyed = false;

  id: string;

  data: VisualizationItemTask;

  appStore: AppStore;

  getAll: () => VisualizationItemTask[];

  options: TaskRowNodeOptions;

  #rowIndex: number | null;

  displayed = true;

  rowPinned = undefined;

  selectable = true;

  rowHeight = undefined;

  rowTop = null;

  group = undefined;

  firstChild = false;

  lastChild = false;

  childIndex = -1;

  level = 0;

  uiLevel = 0;

  stub = false;

  failedLoad = false;

  sourceRowIndex = -1;

  quickFilterAggregateText = null;

  master = false;

  detail = false;

  field = null;

  key = null;

  groupData = null;

  aggData = undefined;

  rowGroupColumn = null;

  rowGroupIndex = null;

  expanded = false;

  leafGroup = false;

  allLeafChildren = [];

  allChildrenCount = null;

  childrenAfterGroup = null;

  childrenAfterSort = null;

  childrenAfterFilter = null;

  footer = false;

  sibling: IRowNode<VisualizationItemTask>;

  public get primaryRow(): IRowNode<VisualizationItemTask> {
    return this;
  }

  constructor(
    task: { duid: string; absentee?: boolean },
    appStore: AppStore,
    getAll: () => VisualizationItemTask[],
    options?: TaskRowNodeOptions
  ) {
    this.id = task.duid;
    this.data = makeVisualizationItemTask(task);
    this.appStore = appStore;
    this.getAll = getAll;
    this.options = options ?? {};
    this.#rowIndex = options?.rowIndex ?? null;
    this.sibling = this;
  }

  public get parent(): IRowNode<VisualizationItemTask> | null {
    const dataStore = this.appStore.$useDataStore();
    if (this.data.itemType !== VisualizationItemType.TASK) {
      return null;
    }
    const task = dataStore.getTaskByDuidIfLoaded(this.data.duid);
    if (task === undefined) {
      return null;
    }
    const parent = dataStore.getParentTaskIfLoaded(task);
    if (parent === undefined) {
      return null;
    }
    return new TaskRowNode(parent, this.appStore, this.getAll);
  }

  public get rowIndex(): number | null {
    if (this.#rowIndex !== null) {
      return this.#rowIndex;
    }
    const res = this.getAll().findIndex((e) => e.duid === this.id);
    if (res === -1) {
      return null;
    }
    this.#rowIndex = res;
    return this.#rowIndex;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSelected(newValue: boolean, clearSelection?: boolean, source?: SelectionEventSourceType) {
    const { selectedOverride } = this.options;
    if (selectedOverride !== undefined) {
      return;
    }
    if (clearSelection) {
      this.appStore.selectedTaskDuids = new Set();
    }
    if (newValue) {
      this.appStore.selectedTaskDuids.add(this.id);
    } else {
      this.appStore.selectedTaskDuids.delete(this.id);
    }
  }

  isSelected() {
    const { selectedOverride } = this.options;
    if (selectedOverride !== undefined) {
      return selectedOverride;
    }
    return this.appStore.selectedTaskDuids.has(this.id);
  }

  // eslint-disable-next-line class-methods-use-this
  isRowPinned() {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  isExpandable() {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  setExpanded(expanded: boolean, e?: MouseEvent | KeyboardEvent) {
    // Expansion is unsupported by this lightweight row node.
  }

  // eslint-disable-next-line class-methods-use-this
  isFullWidthCell() {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  isHovered() {
    return false;
  }

  // oxlint-disable-next-line class-methods-use-this
  addEventListener: IRowNode<VisualizationItemTask>["addEventListener"] = (_eventType, _listener) => {
    // Unsupported
  };

  // oxlint-disable-next-line class-methods-use-this
  removeEventListener: IRowNode<VisualizationItemTask>["removeEventListener"] = (_eventType, _listener) => {
    // Unsupported
  };

  // eslint-disable-next-line class-methods-use-this
  resetQuickFilterAggregateText() {
    // Unsupported
  }

  depthFirstSearch(callback: (rowNode: IRowNode<VisualizationItemTask>) => void) {
    callback(this);
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  setRowHeight(rowHeight: number | undefined | null, estimated?: boolean) {
    // Unsupported
  }

  setData(data: VisualizationItemTask) {
    this.data = data;
  }

  updateData(data: VisualizationItemTask) {
    this.data = data;
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  setDataValue(colKey: string | Column, newValue: any, eventSource?: string) {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-explicit-any
  getDataValue(_colKey: ColKey, _from?: string): any {
    return undefined;
  }

  // eslint-disable-next-line class-methods-use-this
  getAggregatedChildren(
    _colKey: ColKey<VisualizationItemTask> | null | undefined,
    _recursive?: boolean
  ): IRowNode<VisualizationItemTask>[] {
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  getRoute() {
    return undefined;
  }
}

const isTaskNotGroupOrButton = (e: VisualizationItem | undefined): e is VisualizationItemTask =>
  e?.itemType === VisualizationItemType.TASK;

const isRowTaskNotGroupOrButton = (
  e: IRowNode<VisualizationItem> | IRowNode<VisualizationItemTask> | undefined
): e is IRowNode<VisualizationItemTask> => !!e && isTaskNotGroupOrButton(e.data);

export const getListVisualization = (
  getListOrRoadmap: () => HardDisplayTaskContainer | null,
  getApi: () => GridApi<VisualizationItem> | null
): Visualization => ({
  count() {
    return getApi()?.getDisplayedRowCount() ?? 0;
  },
  getSelected() {
    return this.getSelectedRows()
      .map((e) => e.data)
      .filter(isDefined);
  },
  getAll() {
    const res: VisualizationItemTask[] = [];
    getApi()?.forEachNode((e) => {
      if (!isRowTaskNotGroupOrButton(e)) {
        return;
      }
      if (!e.data) {
        return;
      }
      res.push(e.data);
    });
    return res.filter(isTaskNotGroupOrButton);
  },
  getChildren(duid) {
    const row = duid ? this.getRow(duid) : this.getRowByIndex(0)?.parent;
    return row?.childrenAfterFilter?.map((e) => e.data).filter(isDefined) ?? [];
  },
  selectAll() {
    getApi()?.selectAll("filtered");
  },
  deselectAll() {
    getApi()?.deselectAll();
  },
  scrollTo(duid) {
    const row = this.getRow(duid);
    if (!row) {
      return;
    }

    getApi()?.ensureNodeVisible(row as unknown as IRowNode<VisualizationItem>);
  },
  selectAndScrollTo(duid, options = {}) {
    const { deselectOthers = true } = options;
    const row = this.getRow(duid);
    if (!row) {
      return;
    }
    row.setSelected(true, deselectOthers);

    this.scrollTo(duid);
  },
  getHtml(duid) {
    return document.querySelector(`[row-id="${duid}"]`);
  },
  editTitle(duid) {
    const row = this.getRow(duid);
    if (!row) {
      return;
    }

    const renderer = getApi()?.getCellRendererInstances({
      rowNodes: [row as unknown as IRowNode<VisualizationItem>],
      columns: ["title", "ag-Grid-AutoColumn"],
    })[0];
    if (!renderer) {
      return;
    }

    (renderer as unknown as { $root: typeof TitleCellEditor }).$root.focus();
  },
  async hardDisplayTask(task) {
    await getListOrRoadmap?.()?.hardDisplayTask(task);
  },
  getRow(duid) {
    const row = getApi()?.getRowNode(duid);
    return isRowTaskNotGroupOrButton(row) ? row : undefined;
  },
  getRowByIndex(index) {
    const row = getApi()?.getDisplayedRowAtIndex(index);
    return isRowTaskNotGroupOrButton(row) ? row : undefined;
  },
  getSelectedRows() {
    const nodes = getApi()?.getSelectedNodes() ?? [];
    return nodes
      .filter((row) => isRowTaskNotGroupOrButton(row))
      .map((row) => row as unknown as IRowNode<VisualizationItemTask>);
  },
  getAllRows() {
    const res: IRowNode<VisualizationItemTask>[] = [];
    getApi()?.forEachNode((e) => {
      if (!isRowTaskNotGroupOrButton(e)) {
        return;
      }
      res.push(e);
    });
    return res;
  },
});

export const getBoardCalendarOrWorkflowVisualization = (
  getBoardCalendarOrWorkflow: () => TaskVisualizationContainer | null,
  appStore: AppStore
): Visualization => ({
  count() {
    return this.getAll().length;
  },
  getSelected() {
    const dataStore = appStore.$useDataStore();
    return dataStore
      .getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], { includeTrashed: false, includeDraft: false })
      .map(makeVisualizationItemTask);
  },
  getAll() {
    return getBoardCalendarOrWorkflow()?.getAllTasks()?.map(makeVisualizationItemTask) ?? [];
  },
  getChildren(duid) {
    const dataStore = appStore.$useDataStore();

    if (duid === null) {
      return this.getAll().filter((e) => {
        const task = dataStore.getTaskByDuidIfLoaded(e.duid);
        return task === undefined || !dataStore.getParentTaskIfLoaded(task);
      });
    }
    const task = dataStore.getTaskByDuidIfLoaded(duid);
    if (task === undefined) {
      return [];
    }

    const subtaskDuids = new Set(
      dataStore.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, true).map((e) => e.targetDuid)
    );
    return this.getAll().filter((e) => subtaskDuids.has(e.duid));
  },
  selectAll() {
    // eslint-disable-next-line no-param-reassign
    appStore.selectedTaskDuids = new Set(this.getAll().map((e) => e.duid));
  },
  deselectAll() {
    // eslint-disable-next-line no-param-reassign
    appStore.selectedTaskDuids = new Set();
  },
  scrollTo(duid) {
    getBoardCalendarOrWorkflow()?.scrollToTask(duid);
  },
  selectAndScrollTo(duid, options = {}) {
    const { deselectOthers = true } = options;
    if (!deselectOthers) {
      appStore.selectedTaskDuids.add(duid);
      return;
    }
    // eslint-disable-next-line no-param-reassign
    appStore.selectedTaskDuids = new Set([duid]);
    this.scrollTo(duid);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getHtml(duid) {
    // TODO visualization
    return null;
  },
  editTitle(duid) {
    getBoardCalendarOrWorkflow()?.startEditingTask(duid);
  },
  async hardDisplayTask(task) {
    await getBoardCalendarOrWorkflow()?.hardDisplayTask(task);
  },
  getRow(duid) {
    const dataStore = appStore.$useDataStore();
    // TODO incremental this should only return a row if the task is in the visualization
    const task = dataStore.getTaskByDuidIfLoaded(duid);
    if (task === undefined) {
      return undefined;
    }
    return new TaskRowNode(task, appStore, this.getAll);
  },
  getRowByIndex(index) {
    const task = this.getAll()[index];
    if (task === undefined) {
      return undefined;
    }
    return new TaskRowNode(task, appStore, this.getAll, { rowIndex: index });
  },
  getSelectedRows() {
    return this.getSelected().map((e) => new TaskRowNode(e, appStore, this.getAll));
  },
  getAllRows() {
    return this.getAll().map((e, rowIndex) => new TaskRowNode(e, appStore, this.getAll, { rowIndex }));
  },
});

export const getInboxVisualization = (
  getInbox: () => InstanceType<typeof InboxView> | null,
  appStore: AppStore
): Visualization => ({
  count() {
    throw Error("Inbox visualization: count() not implemented");
  },
  getSelected() {
    return getInbox()?.getSelectedTasks() ?? [];
  },
  getAll() {
    // TODO visualization
    return [];
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getChildren(duid) {
    throw Error("Inbox visualization: getChildren() not implemented");
  },
  selectAll() {
    // Inbox selection is managed by the inbox component
  },
  deselectAll() {
    // Inbox selection is managed by the inbox component
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  scrollTo(duid) {
    // Inbox scrolling is managed by the inbox component
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectAndScrollTo(duid, options) {
    // Inbox selection and scrolling are managed by the inbox component
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getHtml(duid) {
    // TODO visualization
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editTitle(duid) {
    // Inbox title editing is managed by the inbox component.
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async hardDisplayTask(task) {
    throw Error("Inbox visualization: hardDisplayTask() not implemented");
  },
  getRow(duid) {
    // TODO incremental this should only return a row if the task is in the visualization
    const dataStore = appStore.$useDataStore();
    const task = dataStore.getTaskByDuidIfLoaded(duid);
    if (task === undefined) {
      return undefined;
    }
    return new TaskRowNode(task, appStore, this.getAll);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getRowByIndex(index) {
    throw Error("Inbox visualization: getRowByIndex() not implemented");
  },
  getSelectedRows() {
    return this.getSelected().map((e) => new TaskRowNode(e, appStore, this.getAll, { selectedOverride: true }));
  },
  getAllRows() {
    throw Error("Inbox visualization: getAllRows() not implemented");
  },
});

export const getDefaultVisualization = (getComponent: () => unknown, appStore: AppStore): Visualization => ({
  count() {
    throw Error("Default visualization: count() not implemented");
  },
  getSelected() {
    const dataStore = appStore.$useDataStore();
    return dataStore
      .getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], { includeTrashed: false, includeDraft: false })
      .map(makeVisualizationItemTask);
  },
  getAll() {
    // TODO visualization
    return [];
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getChildren(duid) {
    throw Error("Default visualization: getChildren() not implemented");
  },
  selectAll() {
    // The default visualization has no backing component to select
  },
  deselectAll() {
    // eslint-disable-next-line no-param-reassign
    appStore.selectedTaskDuids = new Set();
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  scrollTo(duid) {
    // The default visualization has no backing component to scroll
  },
  selectAndScrollTo(duid, options = {}) {
    const { deselectOthers = true } = options;
    if (!deselectOthers) {
      appStore.selectedTaskDuids.add(duid);
      return;
    }
    // eslint-disable-next-line no-param-reassign
    appStore.selectedTaskDuids = new Set([duid]);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getHtml(duid) {
    // TODO visualization
    return null;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editTitle(duid) {
    // The default visualization has no backing component to edit
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async hardDisplayTask(task) {
    throw Error("Default visualization: hardDisplayTask() not implemented");
  },
  getRow(duid) {
    const dataStore = appStore.$useDataStore();
    // TODO incremental this should only return a row if the task is in the visualization
    const task = dataStore.getTaskByDuidIfLoaded(duid);
    if (task === undefined) {
      return undefined;
    }
    return new TaskRowNode(task, appStore, this.getAll);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getRowByIndex(index) {
    throw Error("Default visualization: getRowByIndex() not implemented");
  },
  getSelectedRows() {
    return this.getSelected().map((e) => new TaskRowNode(e, appStore, this.getAll, { selectedOverride: true }));
  },
  getAllRows() {
    throw Error("Default visualization: getAllRows() not implemented");
  },
});
